import React, { useEffect, useState } from "react";

// material-ui

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Stack,
  TextField,
  useTheme,
  Autocomplete,
} from "@mui/material";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports
import CircularWithPath from "../../../../../components/@extended/progress/CircularWithPath";

import { openSnackbar } from "../../../../../api/snackbar";

// assets

// types
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableModal from "../../../../../components/ReusableComponent/ReusableModal";

import {
  CompanyReplyData,
  ReplyApplicant,
  Resume,
  Vacancy,
} from "../../../../../types/companies-managment/companyReply";

// constant
const getInitialValues = (companyReply: CompanyReplyData | null) => {
  if (companyReply) {
    return {
      resume_id: companyReply.resume_id
        ? companyReply.resume_id.toString()
        : "",
      vacancy_id: companyReply.vacancy.id
        ? companyReply.vacancy.id.toString()
        : "",
      reply_to_applicant_id: companyReply.reply_to_applicant_id
        ? companyReply.reply_to_applicant_id.toString()
        : "",
    };
  } else {
    return {
      vacancy_id: "",
      resume_id: "",
      reply_to_applicant_id: "",
    };
  }
};
// ==============================|| COMPANY COMMENT ADD / EDIT - FORM ||============================== //

const AddEditCompanyReply = ({
  open,
  companyReply,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading: boolean;
  id: string;
  setLoading: any;
  fetchData: any;
  companyReply: CompanyReplyData | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const intl = useIntl();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [resumePage, setResumePage] = useState(1);
  const [resumeLastPage, setResumeLastPage] = useState(1);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeInput, setResumeInput] = useState("");
  const [replyApplicants, setReplyApplicants] = useState<ReplyApplicant[]>([]);
  const theme = useTheme();

  useEffect(() => {
    setLoading(false);
    fetchResumes();
    fetchVacancies();
    fetchReplyApplicants();
  }, []);

  const fetchResumes = async (page = 1, search = "", append = false) => {
    setResumeLoading(true);
    try {
      const response = await USERAXIOS.get(`/resumes`);
      const data = response.data.data;
      setResumes(data);
    } catch (error) {
      console.error("Error fetching resumes:", error);
    }
    setResumeLoading(false);
  };

  const fetchVacancies = async () => {
    try {
      const response = await USERAXIOS.get(`/vacancies?page=1`);
      setVacancies(response.data.data);
    } catch (error) {
      console.error("Error fetching resume:", error);
    }
  };

  const fetchReplyApplicants = async () => {
    try {
      const response = await USERAXIOS.get(`/reply_to_application?page=1`);
      setReplyApplicants(response.data.data);
    } catch (error) {
      console.error("Error fetching reply applicants:", error);
    }
  };

  const CompanyReplySchema = Yup.object().shape({
    resume_id: Yup.string().required("Resume is required"),
    vacancy_id: Yup.string().required("Vacancy is required"),
    reply_to_applicant_id: Yup.string().required("Reply applicant is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(companyReply!),
    validationSchema: CompanyReplySchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const companyReplyData = {
          resume_id: Number(values.resume_id),
          vacancy_id: Number(values.vacancy_id),
          reply_to_applicant_id: Number(values.reply_to_applicant_id),
        };

        if (companyReply) {
          const response = await USERAXIOS.post(
            `/company_replies/${companyReply?.id}`,
            companyReplyData
          );
          const updatedCompanyReply = response.data.data;

          if (updatedCompanyReply) {
            setResult((prev) =>
              prev.map((reply) =>
                reply.id === updatedCompanyReply.id
                  ? updatedCompanyReply
                  : reply
              )
            );

            openSnackbar({
              open: true,
              message: "Company Reply Edited Successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(
            `/company_replies`,
            companyReplyData
          );

          const newCompanyReply = response.data.data;

          if (newCompanyReply) {
            setResult((prev) => [...prev, newCompanyReply]);

            openSnackbar({
              open: true,
              message: "Company Reply Added Successfully!",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );
  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle
            color="primary"
            style={{
              textAlign: "center",
              fontSize: "22px",
              fontWeight: "bold",
              marginBottom: "10px",
            }}
          >
            {companyReply ? (
              <FormattedMessage id="Edit Company Reply" />
            ) : (
              <FormattedMessage id="New Company Reply" />
            )}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container>
              <Grid item xs={12} md={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="resume-autocomplete">
                    <FormattedMessage id="Resume" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <FormControl
                    fullWidth
                    error={Boolean(
                      formik.touched.resume_id && formik.errors.resume_id
                    )}
                  >
                    <Autocomplete
                      id="resume-autocomplete"
                      options={resumes}
                      getOptionLabel={(option) =>
                        option.objectives || option.user_name || ""
                      }
                      // getOptionLabel={(option) => {
                      // return theme.direction === "ltr"
                      //   ? option.note
                      //   : option?.note_trans;
                      // }}
                      value={
                        resumes.find(
                          (pkg) => pkg.id.toString() === formik.values.resume_id
                        ) || null
                      }
                      onChange={(_, value) => {
                        formik.setFieldValue(
                          "resume_id",
                          value ? value.id.toString() : ""
                        );
                      }}
                      onInputChange={(_, value) => {
                        setResumeInput(value);
                      }}
                      loading={resumeLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formik.touched.resume_id && formik.errors.resume_id
                          )}
                          helperText={
                            typeof formik.errors.resume_id === "string" &&
                            formik.touched.resume_id
                              ? formik.errors.resume_id
                              : undefined
                          }
                          placeholder="Select a resume"
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="vacancy--autocomplete">
                    <FormattedMessage id="vacancy " />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <FormControl
                    fullWidth
                    error={Boolean(
                      formik.touched.vacancy_id && formik.errors.vacancy_id
                    )}
                  >
                    <Autocomplete
                      id="vacancy--autocomplete"
                      options={vacancies}
                      getOptionLabel={(option) => {
                        return option.title;
                      }}
                      value={
                        vacancies.find(
                          (pkg) =>
                            pkg.id.toString() === formik.values.vacancy_id
                        ) || null
                      }
                      onChange={(_, value) => {
                        formik.setFieldValue(
                          "vacancy_id",
                          value ? value.id.toString() : ""
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          key={params.id}
                          {...params}
                          error={Boolean(
                            formik.touched.vacancy_id &&
                              formik.errors.vacancy_id
                          )}
                          helperText={
                            typeof formik.errors.vacancy_id === "string" &&
                            formik.touched.vacancy_id
                              ? formik.errors.vacancy_id
                              : undefined
                          }
                          placeholder="Select a vacancy "
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="reply-applicant-autocomplete">
                    <FormattedMessage id="Reply Applicant" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <FormControl
                    fullWidth
                    error={Boolean(
                      formik.touched.reply_to_applicant_id &&
                        formik.errors.reply_to_applicant_id
                    )}
                  >
                    <Autocomplete
                      id="reply-applicant-autocomplete"
                      options={replyApplicants}
                      getOptionLabel={(option) => {
                        return theme.direction === "ltr"
                          ? option.message
                          : option?.message_trans?.ar;
                      }}
                      value={
                        replyApplicants.find(
                          (app) =>
                            app.id.toString() ===
                            formik.values.reply_to_applicant_id
                        ) || null
                      }
                      onChange={(_, value) => {
                        formik.setFieldValue(
                          "reply_to_applicant_id",
                          value ? value.id.toString() : ""
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formik.touched.reply_to_applicant_id &&
                              formik.errors.reply_to_applicant_id
                          )}
                          helperText={
                            typeof formik.errors.reply_to_applicant_id ===
                              "string" && formik.touched.reply_to_applicant_id
                              ? formik.errors.reply_to_applicant_id
                              : undefined
                          }
                          placeholder="Select a reply applicant"
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="center" alignItems="center">
              <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                <Grid item md={2} />
                <Grid item md={4}>
                  <Button
                    disabled={isSubmitting}
                    fullWidth
                    type="submit"
                    variant="contained"
                  >
                    <FormattedMessage id={companyReply ? "Update" : "Submit"} />
                  </Button>
                </Grid>
                <Grid item md={4}>
                  <Button
                    fullWidth
                    color="secondary"
                    variant="outlined"
                    onClick={handleClose}
                  >
                    <FormattedMessage id="Cancel" />
                  </Button>
                </Grid>
                <Grid item md={2} />
              </Stack>
            </Grid>
          </DialogActions>
        </Form>
      </FormikProvider>
    </ReusableModal>
  );
};

export default AddEditCompanyReply;
